import { IconButton, Stack, Typography } from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import React, { useState } from 'react'
import { com } from '@eidu/form'
import { useSnackBar } from '../feedback/SnackBarProvider'
import SemanticLinkField from './SemanticLinkField'
import SemanticLinkDisplay from '../data/SemanticLinkDisplay'
import AuthenticationContext from '../../api/authorization/AuthenticationContext'
import deleteSemanticLink from '../../api/analytics/deleteSemanticLink'
import { logException } from '../../util/Logging'
import SemanticLinkKeyConfiguration = com.eidu.sharedlib.entity.analytics.SemanticLinkKeyConfiguration
import EntityType = com.eidu.sharedlib.entity.type.EntityType
import SemanticLink = com.eidu.sharedlib.entity.analytics.SemanticLink
import SemanticLinkId = com.eidu.sharedlib.entity.analytics.SemanticLinkId

type SemanticLinksItemProps = {
  authContext: AuthenticationContext
  entityType: EntityType
  entityTypes: readonly EntityType[]
  semanticLinks: readonly SemanticLink[]
  semanticLinkKeyConfiguration: SemanticLinkKeyConfiguration
  onSemanticLinksChange: () => void
}

const SemanticLinksItem = ({
  authContext,
  entityType,
  entityTypes,
  semanticLinks,
  semanticLinkKeyConfiguration,
  onSemanticLinksChange,
}: SemanticLinksItemProps) => {
  const [creatingForKey, setCreatingForKey] = useState<SemanticLinkKeyConfiguration | null>(null)

  const { showSnackBar } = useSnackBar()

  const linksForKey = semanticLinks.filter(({ key }) => key.equals(semanticLinkKeyConfiguration.key))
  const canAddLink = linksForKey.length === 0 || semanticLinkKeyConfiguration.duplicatesAllowed

  const handleSave = async () => {
    setCreatingForKey(null)
    onSemanticLinksChange()
  }

  const handleDelete = async (semanticLinkId: SemanticLinkId) => {
    try {
      await deleteSemanticLink({ authContext, semanticLinkId }).then(() => {
        onSemanticLinksChange()
        showSnackBar('Successfully deleted semantic link', 'success')
      })
    } catch (error) {
      showSnackBar('Error deleting semantic link', 'error')
      logException(error)
    }
  }

  return (
    <Stack key={semanticLinkKeyConfiguration.key.toString()} spacing={1}>
      <Stack spacing={1}>
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
          <Typography>{semanticLinkKeyConfiguration.title}</Typography>
          {canAddLink && !creatingForKey && (
            <IconButton color="primary" onClick={() => setCreatingForKey(semanticLinkKeyConfiguration)} size="small">
              <AddIcon />
            </IconButton>
          )}
        </Stack>

        {creatingForKey === semanticLinkKeyConfiguration && (
          <SemanticLinkField
            entityType={entityType.id}
            entityTypes={entityTypes}
            semanticLinkKeyConfiguration={semanticLinkKeyConfiguration}
            authContext={authContext}
            onSave={handleSave}
            onCancel={() => setCreatingForKey(null)}
          />
        )}

        {!(linksForKey.length === 0 && creatingForKey === semanticLinkKeyConfiguration) && (
          <SemanticLinkDisplay
            entityTypeId={entityType.id}
            entityTypes={entityTypes}
            semanticLinkKeyConfiguration={semanticLinkKeyConfiguration}
            semanticLinks={linksForKey}
            onDelete={handleDelete}
          />
        )}
      </Stack>
    </Stack>
  )
}

export default SemanticLinksItem
